/**
 * Error reporting utility
 * 
 * This module provides functions for reporting errors to console
 * and optionally to a monitoring service like Sentry or Bugsnag
 */

/**
 * Logger interface for consistent logging throughout the application
 */
export interface Logger {
  info(message: string, data?: any): void;
  warn(message: string, data?: any): void;
  error(message: string, error?: any): void;
  debug(message: string, data?: any): void;
}

/**
 * Application logger implementation
 */
export const logger: Logger = {
  info(message: string, data?: any): void {
    console.info(`[INFO] ${message}`, data ? data : '');
  },
  
  warn(message: string, data?: any): void {
    console.warn(`[WARNING] ${message}`, data ? data : '');
  },
  
  error(message: string, error?: any): void {
    console.error(`[ERROR] ${message}`);
    if (error) {
      // Only show detailed stack traces in development
      if (process.env.NODE_ENV !== 'production') {
        if (error instanceof Error) {
          console.error(`Details: ${error.message}`);
          console.error(`Stack: ${error.stack}`);
        } else {
          console.error('Details:', error);
        }
      } else {
        // In production, just show the error message
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error(`Details: ${errorMessage}`);
      }
    }
    
    // In a real implementation, you would send this to Sentry, Bugsnag, etc.
    // Example with Bugsnag:
    // bugsnag.notify(error, (event) => {
    //   event.addMetadata('context', { message });
    // });
  },
  
  debug(message: string, data?: any): void {
    if (process.env.NODE_ENV !== 'production') {
      console.debug(`[DEBUG] ${message}`, data ? data : '');
    }
  }
};

/**
 * Report an error with optional context
 * @param error The error to report
 * @param context Additional context for the error
 */
export const reportError = (error: Error | unknown, context: Record<string, any> = {}): void => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  // Log to console
  logger.error(errorObj.message, { context, stack: errorObj.stack });
};

/**
 * Report a warning without throwing an error
 * @param message Warning message
 * @param context Additional context for the warning
 */
export const reportWarning = (message: string, context: Record<string, any> = {}): void => {
  // Log to console
  logger.warn(message, context);
};

/**
 * Handle a rejected promise with proper error reporting
 * @param promise Promise to handle
 * @param context Context for error reporting
 * @returns Promise that never rejects, only resolves with result or error
 */
export const handlePromise = async <T>(
  promise: Promise<T>, 
  context: Record<string, any> = {}
): Promise<{ success: boolean; data?: T; error?: Error }> => {
  try {
    const data = await promise;
    return { success: true, data };
  } catch (error) {
    reportError(error as Error, context);
    return { success: false, error: error as Error };
  }
};